/**
 * 選択中の線区 (route_id) / 方面 (direction_name) を URL クエリと sessionStorage に
 * 同期して保持するカスタムフック。
 *
 * 目的:
 *   - 「画面を更新したら選択している線区・方面を保持してほしい」という要望に対応する。
 *
 * 保持方式 (二段構え):
 *   1. URL クエリ (`?route=京浜&direction=南行`)
 *      - リロードで保持される / URL 共有ができる / ブラウザバックが自然に効く。
 *      - こちらが常に「正」の値。
 *   2. sessionStorage (`oba-core.routeSelection`)
 *      - クエリ無しで開かれた場合 (例: ヘッダーロゴ以外からの `/` 直アクセス、
 *        ブックマーク) の復元元。
 *      - タブを閉じたら消える sessionStorage を使い、別作業者の選択が
 *        残り続けないようにする。
 *
 * NOTE: 線区マスタ (`routes-master.json`) はページ側で非同期取得されるため、
 * 復元値がマスタに存在するかの検証はページ側で行う (`sanitizeSelection`)。
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import type { RouteMaster } from '../data/routes';

/** URL クエリのキー: 線区 */
export const ROUTE_QUERY_KEY = 'route';
/** URL クエリのキー: 方面 */
export const DIRECTION_QUERY_KEY = 'direction';
/** sessionStorage のキー */
export const ROUTE_SELECTION_STORAGE_KEY = 'oba-core.routeSelection';

/**
 * 「全線区」を表す `routeId` のセンチネル値。
 *
 * 背景:
 *   線区ドロップダウンの「(全線区)」は当初 `value=""` (= 空文字) だったが、
 *   空文字は「未選択 (= トップページの線区選択ランディングを表示)」の意味でも
 *   使われているため、全線区を選んだ瞬間にランディング画面へ戻ってしまっていた。
 *   そこで「明示的に全線区を選んだ」状態を空文字と区別するための専用値を導入した。
 *
 * `routes-master.json` の route_id と衝突しないよう `__` で囲んだ値にしている。
 */
export const ALL_ROUTES = '__all__';

export interface RouteSelection {
  /**
   * 選択中の route_id。
   * - 空文字     = 未選択 (トップページの線区選択ランディングを表示)
   * - ALL_ROUTES = 全線区 (線区で絞り込まず一覧 + 地図を表示)
   */
  routeId: string;
  /** 選択中の direction_name (空文字 = 未選択) */
  directionName: string;
}

export interface UseRouteSelectionResult extends RouteSelection {
  /** 線区/方面を更新する (URL クエリ + sessionStorage を同時に更新) */
  setSelection: (routeId: string, directionName: string) => void;
  /** 選択をクリアしてトップページ (線区選択画面) に戻す */
  clearSelection: () => void;
}

/** sessionStorage から選択状態を読み出す。壊れていれば空選択を返す。 */
export function readStoredSelection(): RouteSelection {
  try {
    const raw = window.sessionStorage.getItem(ROUTE_SELECTION_STORAGE_KEY);
    if (!raw) return { routeId: '', directionName: '' };
    const parsed = JSON.parse(raw) as Partial<RouteSelection>;
    return {
      routeId: typeof parsed.routeId === 'string' ? parsed.routeId : '',
      directionName: typeof parsed.directionName === 'string' ? parsed.directionName : '',
    };
  } catch {
    // sessionStorage が使えない環境 (プライベートブラウズ等) や JSON 破損は無視する
    return { routeId: '', directionName: '' };
  }
}

/** sessionStorage へ選択状態を書き込む (失敗しても致命的ではないため握り潰す)。 */
export function writeStoredSelection(selection: RouteSelection): void {
  try {
    window.sessionStorage.setItem(ROUTE_SELECTION_STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // 保存できなくても URL クエリ側で保持されるため無視する
  }
}

/** sessionStorage の選択状態を消す (ヘッダーからトップへ戻る操作で使用)。 */
export function clearStoredSelection(): void {
  try {
    window.sessionStorage.removeItem(ROUTE_SELECTION_STORAGE_KEY);
  } catch {
    // 削除できなくても URL クエリ側が空になるため無視する
  }
}

/**
 * 線区マスタに存在しない値を除去する。
 *
 * - route_id が {@link ALL_ROUTES} (全線区) → そのまま維持し、方面はクリアする
 *   (全線区モードでは方面で絞り込まないため)
 * - route_id がマスタに無い → 線区・方面ともクリア (トップページ表示)
 * - direction_name がその線区に無い → 方面のみクリア
 * - `routes` が空 (マスタ未読込) の場合は検証せずそのまま返す
 */
export function sanitizeSelection(
  routes: readonly RouteMaster[],
  selection: RouteSelection,
): RouteSelection {
  // 「全線区」はマスタに存在しない擬似 route_id なので、検証をスキップして維持する。
  // (ここで弾いてしまうと「(全線区)」選択時にトップページへ戻ってしまう)
  if (selection.routeId === ALL_ROUTES) return { routeId: ALL_ROUTES, directionName: '' };
  if (routes.length === 0) return selection;
  const route = routes.find((r) => r.route_id === selection.routeId);
  if (!route) return { routeId: '', directionName: '' };
  const hasDirection = route.directions.some((d) => d.direction_name === selection.directionName);
  return {
    routeId: route.route_id,
    directionName: hasDirection ? selection.directionName : '',
  };
}

/**
 * 選択中の線区/方面を URL クエリ + sessionStorage で永続化するフック。
 */
export function useRouteSelection(): UseRouteSelectionResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const routeId = searchParams.get(ROUTE_QUERY_KEY) ?? '';
  const directionName = searchParams.get(DIRECTION_QUERY_KEY) ?? '';

  // 初回マウント時、URL にクエリが無ければ sessionStorage から復元する。
  useEffect(() => {
    if (routeId) return;
    const stored = readStoredSelection();
    if (!stored.routeId) return;
    const next = new URLSearchParams(searchParams);
    next.set(ROUTE_QUERY_KEY, stored.routeId);
    if (stored.directionName) next.set(DIRECTION_QUERY_KEY, stored.directionName);
    setSearchParams(next, { replace: true });
    // 初回のみ実行したいので依存は空にする (eslint 例外)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL クエリが変わったら sessionStorage にも反映しておく。
  useEffect(() => {
    writeStoredSelection({ routeId, directionName });
  }, [routeId, directionName]);

  const setSelection = useCallback(
    (nextRouteId: string, nextDirectionName: string): void => {
      const next = new URLSearchParams();
      if (nextRouteId) next.set(ROUTE_QUERY_KEY, nextRouteId);
      if (nextRouteId && nextDirectionName) next.set(DIRECTION_QUERY_KEY, nextDirectionName);
      writeStoredSelection({
        routeId: nextRouteId,
        directionName: nextRouteId ? nextDirectionName : '',
      });
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  const clearSelection = useCallback((): void => {
    setSelection('', '');
  }, [setSelection]);

  return useMemo(
    () => ({ routeId, directionName, setSelection, clearSelection }),
    [routeId, directionName, setSelection, clearSelection],
  );
}
